import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import 'react-calendar/dist/Calendar.css';
import DataTable from 'react-data-table-component';
import IconCalendar from '../../assets/SidebarIcons/icon-calendar-time.svg';
import { getRequest } from '../../helpers/apiHandler';

import './_smsTransaction.scss';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const SMSreportModal = ({ show, onClose, access_token, user_id, date, setDate }) => {
  const [loadingData, setLoadingData] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const perkesoHeaders = () => [
    "date", "transaction_id", "message", "transaction_count",
    "country", "phone_number", "status", "user_id", "name", "email",
    "updated_at"
  ];

  const elfoHeaders = () => [
    "date", "transaction_id", "message", "transaction_count",
    "country", "phone_number", "cost", "status", "user_id", "name",
    "email", "updated_at"
  ];

  const generateCSV = async () => {
    setLoadingDownload(true);
    try {
      // Delay to make the loadingDownload state sets in
      await new Promise((resolve) => setTimeout(resolve, 100));

      const formattedData = data.map((row) => ({
        ...row,
        date: moment(row.date).format('MM/DD/YYYY hh:mm:ss A'),
        updated_at: moment(row.updated_at).format('MM/DD/YYYY hh:mm:ss A'),
      }));

      const headers = white_label_perkeso ? perkesoHeaders() : elfoHeaders();
      const headerRow = headers.map((header) => `"${header}"`).join(',');

      const rows = formattedData.map((row) =>
        headers
          .map((header) => {
            const value = (row[header] || '')
              .toString()
              .replace(/[\r\n\t\f\v\s]+/g, ' ')
              .trim();
            return `"${value.replace(/"/g, '""')}"`;
          })
          .join(',')
      );

      const csvContent = '\ufeff' + headerRow + '\n' + rows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SMS-Report-${moment().format('YYYY-MM-DD')}.csv`;

      document.body.appendChild(link);
      link.click();

      // Wait a bit before cleanup
      await new Promise((resolve) => setTimeout(resolve, 100));

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAlertState({
        message: 'Your SMS report has been successfully exported to CSV.',
        show: true,
        variant: 'success',
      });
    } catch (error) {
      console.error('CSV generation error:', error);
      setAlertState({
        message: `Export failed: ${error.message || 'Unknown error occurred'}`,
        show: true,
        variant: 'danger',
      });
    } finally {
      // Add a small delay before removing loading state
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLoadingDownload(false);
    }
  };

  const getSMSReportData = () => {
    setLoadingData(true);
    const payload = {
      headers: { Authorization: access_token.token },
      params: {
        start_date: date[0] ? moment(date[0]).format('YYYY-MM-DD HH:mm:ss') : null,
        end_date: date[1] ? moment(date[1]).format('YYYY-MM-DD HH:mm:ss') : null,
        user_id: user_id || null,
      },
    };

    getRequest(`/admin/sms-report`, payload, (res) => {
      if (res.success) {
        setData(res.data.sms_data || []);
        setTotalRecords(res.data.total_records || 0);
        setTotalPages(res.data.pages || 0);
      } else {
        setAlertState({
          message: res.message,
          show: true,
          variant: 'danger',
        });
      }
      setLoadingData(false);
    });
  };

  const generateColumns = () => {
    return [
      { name: 'Date', sortable: true, selector: (row) => row.date },
      { name: 'Trx ID', sortable: true, selector: (row) => row.transaction_id },
      { name: 'Message', sortable: true, selector: (row) => row.message },
      { name: 'Trx Count', sortable: true, selector: (row) => row.transaction_count },
      { name: 'Country', sortable: true, selector: (row) => row.country },
      { name: 'Phone', sortable: true, selector: (row) => row.phone_number },
      ...(!white_label_perkeso ? [
        { name: 'Cost', sortable: true, selector: (row) => row.cost }
      ] : []),
      { name: 'Status', sortable: true, selector: (row) => row.status },
    ];
  };

  useEffect(() => {
    setColumns(generateColumns());
  }, []);

  useEffect(() => {
    if (user_id) {
      getSMSReportData();
    }
  }, [date, user_id]);

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <div className="w-100 d-flex justify-content-between align-items-center">
          <Modal.Title>SMS Report of User ID: {user_id}</Modal.Title>
          <div className="d-flex gap-2">
            <DateRangePicker
              value={date}
              onChange={setDate}
              clearIcon={null}
              calendarIcon={<img src={IconCalendar} alt="..." />}
              format="MM-dd-y"
              rangeDivider={<i className="fas fa-long-arrow-alt-right" />}
              dayPlaceholder="DD"
              monthPlaceholder="MM"
              yearPlaceholder="YYYY"
            />

            <Button
              onClick={generateCSV}
              disabled={loadingDownload || loadingData}
            >
              {loadingDownload ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="w-100" style={{ padding: 30 }}>
          <DataTable
            columns={columns}
            data={data}
            progressPending={loadingData}
            pagination
            paginationComponentOptions={{
              rowsPerPageText: 'Items per page:',
            }}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SMSreportModal;